import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Meta (Facebook) pixel.
 *
 * Mounted once at the app root (src/Tracking.js) and persists for the lifetime
 * of the session. Because this is a SPA, the browser never reloads on an
 * in-app navigation, so the pixel has to re-fire PageView itself on every
 * client-side route change. Meta attributes each event to document.location at
 * the moment it fires, so a single fire at bootstrap reports only the landing
 * URL — visitors who arrive on the home page and navigate to /poker never
 * produce a PageView carrying a /poker URL, which is what URL-based
 * retargeting audiences are built from.
 *
 * PageView is keyed on pathname only. Query strings are deliberately ignored
 * so that in-page filters (tournament lists, calendars) don't inflate the
 * PageView count; the event URL Meta records still includes the full URL.
 */
const FacebookPixel = ({ pixelId, locale, events = ["PageView"] }) => {
  const location = useLocation();
  // Whether fbq has been initialized, so setup runs once for the session
  // rather than again on every navigation.
  const initialized = useRef(false);

  useEffect(() => {
    if (!pixelId) return;

    if (!initialized.current) {
      // Ensure fbq is loaded once (the f.fbq guard makes this a no-op if the
      // base script is already present).
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/" + locale + "/fbevents.js");

      window.fbq("init", pixelId);
      initialized.current = true;
    }

    // Fires on the initial load and again on every client-side route change.
    events.forEach((event) => window.fbq("track", event));
    // events/locale are static config and are deliberately out of the deps:
    // `events` is a new array on every render and would fire a PageView on
    // every re-render, and `locale` only feeds the one-time script URL above.
  }, [pixelId, location.pathname]);

  return null;
};

export default FacebookPixel;
